import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Alert, CircularProgress,
  Fade, Chip, Divider, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Smartphone, Copy, Check, RefreshCw, X, MessageSquare,
  Clock, CheckCircle2, XCircle, AlertTriangle, History,
  Plus, QrCode, CircleDollarSign
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const ServiceCard = styled(Box)(() => ({
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 14,
  padding: 24,
  transition: 'all 0.25s',
  '&:hover': {
    border: '1px solid rgba(124,58,237,0.4)',
    background: 'rgba(124,58,237,0.04)',
    transform: 'translateY(-2px)',
  },
}));

const CoinChip = styled(Chip)(() => ({
  background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.08))',
  color: '#F59E0B',
  fontWeight: 700,
  border: '1px solid rgba(245,158,11,0.25)',
  '& .MuiChip-icon': { color: '#F59E0B' },
}));

const statusMap = {
  waiting: { label: 'Aguardando SMS', color: '#F59E0B', icon: Clock },
  waiting_retry: { label: 'Aguardando novo SMS', color: '#F59E0B', icon: Clock },
  code_received: { label: 'Código recebido', color: '#10B981', icon: CheckCircle2 },
  completed: { label: 'Concluído', color: '#10B981', icon: CheckCircle2 },
  cancelled: { label: 'Cancelado', color: '#EF4444', icon: XCircle },
  expired: { label: 'Expirado', color: '#6B7280', icon: AlertTriangle },
};

const FB_ICON = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="6" fill="#1877F2"/>
    <path d="M16.5 12.05h-2.7v7.95h-3.3v-7.95H8.5V9.1h2v-1.9c0-2.7 1.1-4.2 4-4.2h2.5v3h-1.5c-1.2 0-1.2.4-1.2 1.2v1.9h2.7l-.5 2.95z" fill="#fff"/>
  </svg>
);

const WA_ICON = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="6" fill="#25D366"/>
    <path d="M12 3.5A8.5 8.5 0 003.5 12c0 1.5.4 2.9 1.1 4.2L3.5 20.5l4.4-1.1A8.5 8.5 0 1012 3.5zm0 15.5a7 7 0 01-3.6-1l-.3-.1-2.6.7.7-2.5-.2-.3A7 7 0 1112 19zm4-5.2c-.2-.1-1.3-.6-1.5-.7-.2-.1-.4-.1-.5.1-.2.2-.6.7-.7.9-.1.1-.3.2-.5.1-.2-.1-1-.4-1.8-1.2-.7-.6-1.1-1.4-1.3-1.6-.1-.2 0-.3.1-.5.1-.1.2-.3.3-.4.1-.1.1-.2.2-.4 0-.1 0-.3-.1-.4-.1-.1-.5-1.3-.7-1.7-.2-.5-.4-.4-.5-.4h-.5c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.3c.1.2 1.6 2.4 3.8 3.4.5.2.9.4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.3-.5 1.4-1 .2-.5.2-.9.1-1-.1-.1-.2-.2-.5-.3z" fill="#fff"/>
  </svg>
);

const serviceIcons = {
  facebook: FB_ICON,
  whatsapp: WA_ICON,
  facebook_us: FB_ICON,
  facebook_pr: FB_ICON,
  facebook_as: FB_ICON,
  whatsapp_us: WA_ICON,
  whatsapp_pr: WA_ICON,
  whatsapp_as: WA_ICON,
};
const COIN_SUGGESTIONS = [5, 10, 25, 100];

export default function NumeroVirtual() {
  const location = useLocation();
  const initialCountry = (() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('country');
    return c === 'us' ? 'us' : 'br';
  })();

  const [services, setServices] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(initialCountry);

  const [activeActivation, setActiveActivation] = useState(null);
  const [buying, setBuying] = useState(false);
  const [pollingCode, setPollingCode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [copied, setCopied] = useState('');

  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showAddCoins, setShowAddCoins] = useState(false);
  const [selectedCoinAmount, setSelectedCoinAmount] = useState(null);
  const [customCoinValue, setCustomCoinValue] = useState('');
  const [buyingCoins, setBuyingCoins] = useState(false);
  const [pixUrl, setPixUrl] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [pixOrderId, setPixOrderId] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [pixPolling, setPixPolling] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pollingRef = useRef(null);
  const timerRef = useRef(null);
  const pixPollingRef = useRef(null);

  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sms/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setServices(res.data.services);
        setUserCoins(res.data.userCoins);
      }
    } catch { setError('Erro ao carregar serviços'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (pixPollingRef.current) clearInterval(pixPollingRef.current);
    };
  }, []);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const startPolling = useCallback((activationId, expiresAt) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (timerRef.current) clearInterval(timerRef.current);

    const expiry = new Date(expiresAt).getTime();

    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(timerRef.current);
    }, 1000);

    setTimeLeft(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));

    pollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sms/status/${activationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success) {
          const { status, code } = res.data;
          if (status === 'code_received') {
            setPollingCode(code);
            setActiveActivation(prev => prev ? { ...prev, status: 'code_received', code } : prev);
            clearInterval(pollingRef.current);
            clearInterval(timerRef.current);
            pollingRef.current = null;
          } else if (status === 'expired' || status === 'cancelled') {
            setActiveActivation(prev => prev ? { ...prev, status } : prev);
            clearInterval(pollingRef.current);
            clearInterval(timerRef.current);
            pollingRef.current = null;
            if (status === 'expired') setSuccess('Tempo expirado. Coins devolvidos.');
            fetchServices();
          }
        }
      } catch { /* continue */ }
    }, 5000);
  }, [fetchServices]);

  useEffect(() => {
    const checkPending = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sms/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success && res.data.hasPending) {
          const act = res.data.activation;
          setActiveActivation(act);
          if (act.code) {
            setPollingCode(act.code);
          } else {
            startPolling(act.activationId, act.expiresAt);
          }
        }
      } catch { /* ignore */ }
    };
    checkPending();
  }, [startPolling]);

  const handleBuy = async (serviceId) => {
    setError(''); setSuccess(''); setBuying(true); setPollingCode(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sms/buy`,
        { serviceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setActiveActivation(res.data.activation);
        startPolling(res.data.activation.activationId, res.data.activation.expiresAt);
        fetchServices();
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao comprar número'); }
    finally { setBuying(false); }
  };

  const handleConfirm = async () => {
    if (!activeActivation) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sms/confirm/${activeActivation.activationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveActivation(prev => ({ ...prev, status: 'completed' }));
      setSuccess('Ativação concluída com sucesso!');
      fetchServices();
    } catch (err) { setError(err.response?.data?.message || 'Erro ao confirmar'); }
  };

  const handleCancel = async () => {
    if (!activeActivation) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sms/cancel/${activeActivation.activationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      if (timerRef.current) clearInterval(timerRef.current);
      setActiveActivation(prev => ({ ...prev, status: 'cancelled' }));
      setSuccess(res.data.message || 'Cancelado. Coins devolvidos.');
      fetchServices();
    } catch (err) { setError(err.response?.data?.message || 'Erro ao cancelar'); }
  };

  const handleRetry = async () => {
    if (!activeActivation) return;
    setError(''); setPollingCode(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sms/retry/${activeActivation.activationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveActivation(prev => ({ ...prev, status: 'waiting_retry', code: null }));
      startPolling(activeActivation.activationId, activeActivation.expiresAt);
    } catch (err) { setError(err.response?.data?.message || 'Erro ao solicitar novo SMS'); }
  };

  const handleNewNumber = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (timerRef.current) clearInterval(timerRef.current);
    setActiveActivation(null);
    setPollingCode(null);
    setTimeLeft(0);
    setError('');
    setSuccess('');
  };

  // --- Coins purchase ---
  const handleBuyCoins = async (amount) => {
    setError(''); setBuyingCoins(true); setPixUrl(''); setPixOrderId('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sms/buy-coins`,
        { valor: amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setPixUrl(res.data.checkout_url);
        setPixOrderId(res.data.order_id);
        startPixPolling(res.data.order_id);
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro ao gerar pagamento'); }
    finally { setBuyingCoins(false); }
  };

  const startPixPolling = (orderId) => {
    if (pixPollingRef.current) clearInterval(pixPollingRef.current);
    setPixPolling(true);

    pixPollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sms/coin-status/${orderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.status === 'paid') {
          clearInterval(pixPollingRef.current);
          pixPollingRef.current = null;
          setPixPolling(false);
          setPixUrl('');
          setPixOrderId('');
          setShowAddCoins(false);
          setSelectedCoinAmount(null);
          setSuccess(`${selectedCoinAmount} coins adicionados ao seu saldo!`);
          fetchServices();
        }
      } catch { /* continue */ }
    }, 4000);
  };

  const handleCloseAddCoins = () => {
    if (pixPollingRef.current) { clearInterval(pixPollingRef.current); pixPollingRef.current = null; }
    setShowAddCoins(false);
    setSelectedCoinAmount(null);
    setCustomCoinValue('');
    setPixUrl('');
    setPixOrderId('');
    setPixPolling(false);
    setBuyingCoins(false);
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sms/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setHistory(res.data.activations);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const formatPhone = (p) => {
    if (!p) return '';
    const clean = p.replace(/\D/g, '');
    if (clean.length === 13) return `+${clean.slice(0,2)} (${clean.slice(2,4)}) ${clean.slice(4,9)}-${clean.slice(9)}`;
    if (clean.length === 12) return `+${clean.slice(0,2)} (${clean.slice(2,4)}) ${clean.slice(4,8)}-${clean.slice(8)}`;
    return p;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress size={32} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Smartphone size={22} color="#7C3AED" />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.02em' }}>
                Números Virtuais
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CoinChip
                icon={<CircleDollarSign size={15} />}
                label={`${userCoins} coin${userCoins !== 1 ? 's' : ''}`}
              />
              <Button
                size="small" variant="outlined" startIcon={<Plus size={14} />}
                onClick={() => setShowAddCoins(true)}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                  borderColor: 'rgba(245,158,11,0.3)', color: '#F59E0B',
                  '&:hover': { borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)' }
                }}
              >
                Adicionar
              </Button>
              <Tooltip title="Histórico">
                <IconButton size="small" onClick={() => { setShowHistory(true); fetchHistory(); }}
                  sx={{ color: '#6B7280', '&:hover': { color: '#A78BFA' } }}>
                  <History size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Compre números temporários para receber códigos de verificação
          </Typography>

          {(!activeActivation || ['completed', 'cancelled', 'expired'].includes(activeActivation?.status)) && (
            <Box sx={{ mt: 2.5 }}>
              <ToggleButtonGroup
                exclusive
                value={country}
                onChange={(_e, val) => { if (val) setCountry(val); }}
                size="small"
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  p: 0.5,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '8px !important',
                    color: '#9CA3AF',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2.2,
                    py: 0.7,
                    fontSize: '0.85rem',
                    '&.Mui-selected': {
                      background: 'rgba(124,58,237,0.18)',
                      color: '#A78BFA',
                      '&:hover': { background: 'rgba(124,58,237,0.22)' },
                    },
                  },
                }}
              >
                <ToggleButton value="br">🇧🇷 Brasil</ToggleButton>
                <ToggleButton value="us">🇺🇸 Estados Unidos</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        {!activeActivation || ['completed', 'cancelled', 'expired'].includes(activeActivation?.status) ? (
          (() => {
            const filtered = services.filter(s => (s.country || 'br') === country);
            if (filtered.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', py: 6, color: '#6B7280' }}>
                  <Smartphone size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    Nenhum serviço disponível para este país no momento.
                  </Typography>
                </Box>
              );
            }
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                {filtered.map(svc => {
                  const Icon = serviceIcons[svc.id] || FB_ICON;
                  const isFacebook = String(svc.id).startsWith('facebook');
                  const buttonBg = isFacebook ? '#1877F2' : '#25D366';
                  const buttonHover = isFacebook ? '#1565C0' : '#1DA851';
                  const numberLabel = svc.country === 'us'
                    ? `Número +1 de ${svc.countryLabel || 'Estados Unidos'}`
                    : 'Número brasileiro válido';
                  const subtitleLabel = svc.country === 'us'
                    ? `Virtual number · ${svc.countryLabel || 'USA'}`
                    : 'Número temporário BR';
                  return (
                    <ServiceCard key={svc.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                        <Icon />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 700, color: '#F9FAFB', fontSize: '1.05rem' }}>{svc.name}</Typography>
                            {svc.flag && (
                              <Typography component="span" sx={{ fontSize: '1rem' }}>{svc.flag}</Typography>
                            )}
                          </Box>
                          <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>{subtitleLabel}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <CircleDollarSign size={18} color="#F59E0B" style={{ alignSelf: 'center' }} />
                          <Typography sx={{ color: '#F59E0B', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1 }}>
                            {svc.coins}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.82rem' }}>
                          coin{svc.coins > 1 ? 's' : ''} <span style={{ color: '#4B5563' }}>(R$ {svc.coins},00)</span>
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle2 size={13} color="#10B981" />
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>{numberLabel}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle2 size={13} color="#10B981" />
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Receba o código em até 15 min</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle2 size={13} color="#10B981" />
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Reembolso se não receber</Typography>
                        </Box>
                      </Box>

                      <Button
                        fullWidth variant="contained"
                        disabled={!svc.canBuy || buying}
                        onClick={() => handleBuy(svc.id)}
                        startIcon={buying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Smartphone size={15} />}
                        sx={{
                          background: buttonBg,
                          textTransform: 'none', fontWeight: 600, py: 1.2, fontSize: '0.88rem',
                          '&:hover': { background: buttonHover },
                          '&.Mui-disabled': { background: 'rgba(107,114,128,0.2)', color: '#4B5563' }
                        }}
                      >
                        {buying ? 'Comprando...' : !svc.canBuy ? 'Coins insuficientes' : 'Comprar Número'}
                      </Button>

                      {!svc.canBuy && !buying && (
                        <Button
                          fullWidth size="small" startIcon={<Plus size={13} />}
                          onClick={() => setShowAddCoins(true)}
                          sx={{ mt: 1, textTransform: 'none', color: '#F59E0B', fontSize: '0.78rem', '&:hover': { background: 'rgba(245,158,11,0.08)' } }}
                        >
                          Adicionar coins
                        </Button>
                      )}
                    </ServiceCard>
                  );
                })}
              </Box>
            );
          })()
        ) : (
          <SectionCard elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <MessageSquare size={20} color="#7C3AED" />
                <Typography sx={{ fontWeight: 700, color: '#F9FAFB', fontSize: '1.05rem' }}>
                  {activeActivation.serviceName}
                </Typography>
              </Box>
              {(() => {
                const st = statusMap[activeActivation.status] || statusMap.waiting;
                const StIcon = st.icon;
                return (
                  <Chip
                    icon={<StIcon size={13} />}
                    label={st.label}
                    size="small"
                    sx={{ background: `${st.color}20`, color: st.color, fontWeight: 600, fontSize: '0.75rem', '& .MuiChip-icon': { color: st.color } }}
                  />
                );
              })()}
            </Box>

            <Box sx={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 3, p: 3, textAlign: 'center', mb: 3 }}>
              <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Seu número temporário
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <Typography sx={{ color: '#F9FAFB', fontWeight: 800, fontSize: '1.7rem', letterSpacing: '0.03em', fontFamily: 'monospace' }}>
                  {formatPhone(activeActivation.phoneNumber)}
                </Typography>
                <Tooltip title={copied === 'phone' ? 'Copiado!' : 'Copiar número'}>
                  <IconButton onClick={() => handleCopy(activeActivation.phoneNumber, 'phone')} size="small">
                    {copied === 'phone' ? <Check size={16} color="#10B981" /> : <Copy size={16} color="#A78BFA" />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ color: '#4B5563', fontSize: '0.78rem', mt: 1 }}>
                Use este número na verificação do {activeActivation.serviceName}
              </Typography>
            </Box>

            {activeActivation.status === 'waiting' || activeActivation.status === 'waiting_retry' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={Math.max(0, (timeLeft / 900) * 100)}
                      size={60} thickness={3}
                      sx={{ color: timeLeft < 120 ? '#EF4444' : '#7C3AED' }}
                    />
                    <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: timeLeft < 120 ? '#EF4444' : '#D1D5DB', fontWeight: 700, fontSize: '0.82rem', fontFamily: 'monospace' }}>
                        {formatTime(timeLeft)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#D1D5DB', fontWeight: 600, fontSize: '0.9rem' }}>
                      Aguardando código...
                    </Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>
                      O SMS será detectado automaticamente
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="outlined" size="small" startIcon={<X size={14} />}
                    onClick={handleCancel}
                    sx={{ textTransform: 'none', borderColor: 'rgba(239,68,68,0.3)', color: '#EF4444', '&:hover': { borderColor: '#EF4444', background: 'rgba(239,68,68,0.08)' } }}
                  >
                    Cancelar e devolver coins
                  </Button>
                </Box>
              </>
            ) : null}

            {pollingCode || activeActivation.code ? (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 3 }} />
                <Box sx={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 3, p: 3, textAlign: 'center' }}>
                  <CheckCircle2 size={24} color="#10B981" style={{ marginBottom: 8 }} />
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Código recebido
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Typography sx={{ color: '#10B981', fontWeight: 800, fontSize: '2.2rem', letterSpacing: '0.15em', fontFamily: 'monospace' }}>
                      {pollingCode || activeActivation.code}
                    </Typography>
                    <Tooltip title={copied === 'code' ? 'Copiado!' : 'Copiar código'}>
                      <IconButton onClick={() => handleCopy(pollingCode || activeActivation.code, 'code')} size="small">
                        {copied === 'code' ? <Check size={16} color="#10B981" /> : <Copy size={16} color="#10B981" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                  {activeActivation.status !== 'completed' && (
                    <>
                      <Button
                        variant="contained" startIcon={<CheckCircle2 size={14} />}
                        onClick={handleConfirm}
                        sx={{ background: '#10B981', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#059669' } }}
                      >
                        Confirmar e finalizar
                      </Button>
                      <Button
                        variant="outlined" startIcon={<RefreshCw size={14} />}
                        onClick={handleRetry}
                        sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.3)', color: '#A78BFA', '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' } }}
                      >
                        Outro SMS
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outlined" startIcon={<Smartphone size={14} />}
                    onClick={handleNewNumber}
                    sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.1)', color: '#9CA3AF', '&:hover': { borderColor: 'rgba(255,255,255,0.2)', color: '#D1D5DB' } }}
                  >
                    Novo número
                  </Button>
                </Box>
              </Box>
            ) : null}
          </SectionCard>
        )}

        {/* Modal Adicionar Coins */}
        <Dialog
          open={showAddCoins}
          onClose={handleCloseAddCoins}
          maxWidth="xs" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircleDollarSign size={20} color="#F59E0B" />
              <Typography sx={{ fontWeight: 700, color: '#F9FAFB' }}>Adicionar Coins</Typography>
            </Box>
            <IconButton onClick={handleCloseAddCoins} size="small" sx={{ color: '#6B7280' }}>
              <X size={18} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {!pixUrl ? (
                <>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', mb: 2.5 }}>
                    1 coin = R$ 1,00. Selecione o valor ou digite um personalizado:
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                    {COIN_SUGGESTIONS.map(amount => (
                      <Box
                        key={amount}
                        onClick={() => { setSelectedCoinAmount(amount); setCustomCoinValue(''); }}
                        sx={{
                          p: 2, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                          transition: 'all 0.2s',
                          border: selectedCoinAmount === amount && !customCoinValue ? '2px solid #F59E0B' : '1px solid rgba(255,255,255,0.08)',
                          background: selectedCoinAmount === amount && !customCoinValue ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.02)',
                          '&:hover': { background: 'rgba(245,158,11,0.06)', borderColor: 'rgba(245,158,11,0.4)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                          <CircleDollarSign size={16} color="#F59E0B" />
                          <Typography sx={{ fontWeight: 800, color: '#F59E0B', fontSize: '1.2rem' }}>
                            {amount}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                          R$ {amount},00
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <TextField
                    fullWidth size="small"
                    placeholder="Outro valor"
                    type="number"
                    value={customCoinValue}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setCustomCoinValue(val);
                      if (val && parseInt(val, 10) >= 5) {
                        setSelectedCoinAmount(parseInt(val, 10));
                      } else {
                        setSelectedCoinAmount(null);
                      }
                    }}
                    error={customCoinValue !== '' && parseInt(customCoinValue, 10) < 5}
                    helperText={customCoinValue !== '' && parseInt(customCoinValue, 10) < 5 ? 'Valor mínimo: R$ 5,00' : ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.9rem' }}>R$</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: customCoinValue && parseInt(customCoinValue, 10) >= 5 ? (
                        <InputAdornment position="end">
                          <Typography sx={{ color: '#F59E0B', fontSize: '0.78rem', fontWeight: 600 }}>
                            = {parseInt(customCoinValue, 10)} coins
                          </Typography>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: '#F9FAFB',
                        background: 'rgba(255,255,255,0.02)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(245,158,11,0.4)' },
                        '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
                      },
                      '& .MuiFormHelperText-root': { color: '#EF4444' },
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      '& input[type=number]': { MozAppearance: 'textfield' },
                    }}
                  />

                  {selectedCoinAmount && selectedCoinAmount >= 5 && (
                    <Box sx={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', borderRadius: 2, p: 2, mb: 1, textAlign: 'center' }}>
                      <Typography sx={{ color: '#D1D5DB', fontSize: '0.85rem' }}>
                        Você receberá <strong style={{ color: '#F59E0B' }}>{selectedCoinAmount} coins</strong> por{' '}
                        <strong style={{ color: '#F59E0B' }}>R$ {selectedCoinAmount},00</strong> via Pix
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <QrCode size={32} color="#F59E0B" style={{ marginBottom: 12 }} />
                  <Typography sx={{ color: '#D1D5DB', fontWeight: 600, mb: 1, fontSize: '0.95rem' }}>
                    Pague via Pix
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', mb: 2 }}>
                    {selectedCoinAmount} coins — R$ {selectedCoinAmount},00
                  </Typography>

                  <Button
                    variant="contained" fullWidth
                    onClick={() => window.open(pixUrl, '_blank')}
                    startIcon={<QrCode size={16} />}
                    sx={{ background: '#F59E0B', color: '#000', fontWeight: 700, textTransform: 'none', mb: 2, '&:hover': { background: '#D97706' } }}
                  >
                    Abrir Pix para pagamento
                  </Button>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <CircularProgress size={14} sx={{ color: '#F59E0B' }} />
                    <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>
                      Aguardando confirmação do pagamento...
                    </Typography>
                  </Box>
                  <Typography sx={{ color: '#4B5563', fontSize: '0.72rem', mt: 1 }}>
                    Os coins serão adicionados automaticamente após o pagamento
                  </Typography>
                </Box>
              )}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            {!pixUrl ? (
              <>
                <Button onClick={handleCloseAddCoins} sx={{ textTransform: 'none', color: '#6B7280' }}>Cancelar</Button>
                <Button
                  variant="contained"
                  disabled={!selectedCoinAmount || selectedCoinAmount < 5 || buyingCoins}
                  onClick={() => handleBuyCoins(selectedCoinAmount)}
                  startIcon={buyingCoins ? <CircularProgress size={14} sx={{ color: '#000' }} /> : <CircleDollarSign size={14} />}
                  sx={{
                    background: '#F59E0B', color: '#000', fontWeight: 700, textTransform: 'none',
                    '&:hover': { background: '#D97706' },
                    '&.Mui-disabled': { background: 'rgba(107,114,128,0.2)', color: '#4B5563' }
                  }}
                >
                  {buyingCoins ? 'Gerando Pix...' : 'Gerar Pix'}
                </Button>
              </>
            ) : (
              <Button onClick={handleCloseAddCoins} sx={{ textTransform: 'none', color: '#6B7280' }}>Fechar</Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Modal Histórico */}
        <Dialog
          open={showHistory}
          onClose={() => setShowHistory(false)}
          maxWidth="sm" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History size={18} color="#7C3AED" />
              <Typography sx={{ fontWeight: 700, color: '#F9FAFB' }}>Histórico</Typography>
            </Box>
            <IconButton onClick={() => setShowHistory(false)} size="small" sx={{ color: '#6B7280' }}>
              <X size={18} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loadingHistory ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#7C3AED' }} />
              </Box>
            ) : history.length === 0 ? (
              <Typography sx={{ color: '#4B5563', textAlign: 'center', py: 4 }}>Nenhuma ativação ainda</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {history.map(h => {
                  const st = statusMap[h.status] || { label: h.status, color: '#6B7280', icon: Clock };
                  const StIcon = st.icon;
                  const chargedCoins = h.coinsCharged || h.tokensCharged || 0;
                  return (
                    <Box key={h._id} sx={{ p: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: '#F9FAFB', fontSize: '0.88rem' }}>{h.serviceName}</Typography>
                          <Chip icon={<StIcon size={11} />} label={st.label} size="small"
                            sx={{ background: `${st.color}15`, color: st.color, fontSize: '0.68rem', height: 20, '& .MuiChip-icon': { color: st.color } }} />
                        </Box>
                        <Typography sx={{ color: '#F59E0B', fontSize: '0.72rem', fontWeight: 600 }}>
                          {chargedCoins} coin{chargedCoins > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {formatPhone(h.phoneNumber)}
                        </Typography>
                        <Typography sx={{ color: '#4B5563', fontSize: '0.72rem' }}>
                          {h.createdAt ? new Date(h.createdAt).toLocaleString('pt-BR') : '-'}
                        </Typography>
                      </Box>
                      {h.code && (
                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: '#10B981', fontSize: '0.82rem', fontWeight: 600, fontFamily: 'monospace' }}>
                            Código: {h.code}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowHistory(false)} sx={{ textTransform: 'none', color: '#9CA3AF' }}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
}
